/**
 * Converts a string to a URL-friendly slug
 * @param text The text to slugify
 * @returns A slugified string with words separated by hyphens
 */
export const slugify = (text: string): string => {
  return (
    text
      .toString()
      .toLowerCase()
      .trim()
      // Remove accents and special characters
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Replace spaces and special characters with hyphens
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      // Remove multiple consecutive hyphens
      .replace(/-+/g, '-')
      // Remove leading and trailing hyphens
      .replace(/^-+/, '')
      .replace(/-+$/, '')
  );
};

/**
 * Converts a slug back to a more readable format
 * @param slug The slug to deslugify
 * @returns A more readable string with words separated by spaces
 */
export const deslugify = (slug: string): string => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Generates a unique slug by checking against existing slugs
 * @param text The text to slugify
 * @param existingSlugs Array of existing slugs to check against
 * @returns A unique slug
 */
export const generateUniqueSlug = (
  text: string,
  existingSlugs: string[] = []
): string => {
  let baseSlug = slugify(text);
  let uniqueSlug = baseSlug;
  let counter = 1;

  while (existingSlugs.includes(uniqueSlug)) {
    uniqueSlug = `${baseSlug}-${counter}`;
    counter++;
  }

  return uniqueSlug;
};

/**
 * Creates a mapping of slugs to project IDs
 * @param projects Array of projects to create mapping from
 * @returns Object with slug as key and project ID as value
 */
export const createSlugToIdMapping = (
  projects: Array<{ id: string | number; name: string }>
): Record<string, string> => {
  const mapping: Record<string, string> = {};
  const usedSlugs: string[] = [];

  projects.forEach((project) => {
    const slug = generateUniqueSlug(
      project.name,
      usedSlugs
    );
    mapping[slug] = project.id.toString();
    usedSlugs.push(slug);
  });

  return mapping;
};

/**
 * Creates a reverse mapping of project IDs to slugs
 * @param projects Array of projects to create mapping from
 * @returns Object with project ID as key and slug as value
 */
export const createIdToSlugMapping = (
  projects: Array<{ id: string | number; name: string }>
): Record<string, string> => {
  const mapping: Record<string, string> = {};
  const usedSlugs: string[] = [];

  projects.forEach((project) => {
    const slug = generateUniqueSlug(
      project.name,
      usedSlugs
    );
    mapping[project.id.toString()] = slug;
    usedSlugs.push(slug);
  });

  return mapping;
};
